// src/lib/uso.js
// Telemetria de USO: anonima, agregada, anti-vigilancia POR CONSTRUCAO.
// Helper unico, fire-and-forget: NUNCA quebra a operacao do balcao.
// Regras invioláveis: sem id de pessoa/PIN/nome/CPF/conteudo digitado;
// tempo so em FAIXA (bucket); sessao volatil e rotativa; allowlist rigida.
import { supabase } from '../config/supabaseClient.js';
import { operadorAtual } from './operador.js';
import { APP_VERSION } from './../config/version.js';

// ---- Allowlists: PII nao tem porta de entrada (valor fora da lista e descartado) ----
const EVENTOS = new Set(['tela_abre', 'acao', 'tarefa_inicio', 'tarefa_fim', 'tarefa_abandono', 'resultado', 'busca_vazia']);
const MODULOS = new Set(['chaves', 'cracha', 'visita_adm', 'cadastros', 'pesquisa', 'kpis']);
const ACOES   = new Set(['emprestar_clicou', 'devolver_clicou', 'cracha_registrar', 'visita_registrar', 'cadastro_salvar', 'rel_exportar', 'filtro_aplicar', 'definir_meta']);
const ALVOS   = new Set(['chave', 'cracha', 'visita', 'colaborador', 'setor']);
const TAREFAS = new Set(['emprestar', 'devolver', 'registrar_cracha', 'registrar_visita', 'cadastro_colaborador']);
const ETAPAS  = new Set(['form_aberto', 'colaborador_escolhido', 'chave_escolhida', 'motivo_preenchido', 'confirmacao', 'informar_setor', 'cpf_matricula']);
const RESULT  = new Set(['ok', 'sucesso', 'erro_rede', 'erro_conflito', 'erro_validacao', 'erro_outro']);
const BUSCAS  = new Set(['colaborador', 'setor', 'chave']);
const FAIXAS  = new Set(['<2s', '2-5s', '5-15s', '15-60s', '>60s']);
const CHAVES_OK = ['evento', 'rota', 'origem_rota', 'modulo', 'acao', 'alvo', 'tarefa', 'etapa', 'resultado', 'tipo_busca', 'faixa_duracao'];

// ---- grupo_posto: colapsa o perfil (anti-reidentificacao de perfil raro) ----
function grupoPosto() {
  const p = operadorAtual.perfil;
  if (p === 'GESTOR' || p === 'SUPERVISOR') return 'GESTAO';
  if (p) return 'RECEPCAO';
  return null;
}

// ---- sessao_anon volatil e rotativa (NAO storage; app e hash-based) ----
function novoId() { return (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now()) + '-' + Math.random(); }
let sessaoAnon = novoId();
let sessaoNasc = Date.now();
function sessao() {
  if (Date.now() - sessaoNasc > 10 * 60 * 1000) { sessaoAnon = novoId(); sessaoNasc = Date.now(); }
  return sessaoAnon;
}
function rotacionarSessao() { sessaoAnon = novoId(); sessaoNasc = Date.now(); }

// ---- toggle + Do-Not-Track (degrada se a migration 0032 ainda nao rodou) ----
let ativa = true;
export async function carregarTelemetria() {
  if (navigator.doNotTrack === '1' || window.doNotTrack === '1') { ativa = false; return; }
  try {
    const { data } = await supabase.from('configuracoes').select('valor').eq('chave', 'telemetriaUsoAtiva').maybeSingle();
    if (data && data.valor === false) ativa = false;
  } catch (e) { /* sem config => segue ativo (tolerante) */ }
}

// ---- normaliza/valida: so passa o que esta na allowlist (chaves E valores) ----
function limpar(dados) {
  const out = {};
  for (const k of CHAVES_OK) {
    const v = dados[k];
    if (v == null) continue;
    if (k === 'evento' && !EVENTOS.has(v)) continue;
    if (k === 'modulo' && !MODULOS.has(v)) continue;
    if (k === 'acao' && !ACOES.has(v)) continue;
    if (k === 'alvo' && !ALVOS.has(v)) continue;
    if (k === 'tarefa' && !TAREFAS.has(v)) continue;
    if (k === 'etapa' && !ETAPAS.has(v)) continue;
    if (k === 'resultado' && !RESULT.has(v)) continue;
    if (k === 'tipo_busca' && !BUSCAS.has(v)) continue;
    if (k === 'faixa_duracao' && !FAIXAS.has(v)) continue;
    if (k === 'rota' || k === 'origem_rota') { out[k] = String(v).split('?')[0]; continue; }
    out[k] = v;
  }
  return out;
}

// ---- buffer + flush em lote (fire-and-forget; nunca propaga erro) ----
const fila = [];
function enfileirar(dados) {
  if (!ativa) return;
  const ev = limpar(dados);
  if (!ev.evento) return;
  ev.grupo_posto = grupoPosto();
  ev.sessao_anon = sessao();
  ev.app_version = APP_VERSION;
  fila.push(ev);
  if (fila.length >= 10) flush();
}
function flush() {
  if (!fila.length || !ativa) return;
  const lote = fila.splice(0);
  supabase.from('evento_uso').insert(lote).then(function () {}, function () {});
}
setInterval(function () { if (fila.length) flush(); }, 8000);
window.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') flush(); });
window.addEventListener('pagehide', flush);

// ===== API publica (1 linha nos pontos de codigo) =====

let rotaAnterior = null;
export function registrarTela(rota) {
  rotacionarSessao();
  enfileirar({ evento: 'tela_abre', rota: rota, origem_rota: rotaAnterior, modulo: moduloDaRota(rota) });
  rotaAnterior = rota;
}
function moduloDaRota(r) {
  if (!r) return null;
  if (r.indexOf('/movimentacao') === 0 || r.indexOf('/chaves') === 0) return 'chaves';
  if (r.indexOf('/cracha') === 0) return 'cracha';
  if (r.indexOf('/visita') === 0) return 'visita_adm';
  if (r.indexOf('/colaboradores') === 0 || r.indexOf('/empregadores') === 0 || r.indexOf('/setores') === 0) return 'cadastros';
  if (r.indexOf('/pesquisa') === 0) return 'pesquisa';
  if (r.indexOf('/dashboard') === 0) return 'kpis';
  return null;
}

export function registrarAcao(modulo, acao, alvo) { enfileirar({ evento: 'acao', modulo: modulo, acao: acao, alvo: alvo }); }
export function registrarResultado(modulo, acao, resultado) { enfileirar({ evento: 'resultado', modulo: modulo, acao: acao, resultado: resultado }); }
export function registrarBuscaVazia(modulo, tipoBusca) { enfileirar({ evento: 'busca_vazia', modulo: modulo, tipo_busca: tipoBusca }); }

function relogio() { return (window.performance && performance.now) ? performance.now() : Date.now(); }
function bucket(ms) {
  if (ms < 2000) return '<2s';
  if (ms < 5000) return '2-5s';
  if (ms < 15000) return '5-15s';
  if (ms < 60000) return '15-60s';
  return '>60s';
}
export function iniciarTarefa(tarefa) {
  const t0 = relogio();
  enfileirar({ evento: 'tarefa_inicio', tarefa: tarefa });
  let fechada = false;
  return {
    concluir: function () { if (fechada) return; fechada = true; enfileirar({ evento: 'tarefa_fim', tarefa: tarefa, resultado: 'sucesso', faixa_duracao: bucket(relogio() - t0) }); },
    abandonar: function (etapa) { if (fechada) return; fechada = true; enfileirar({ evento: 'tarefa_abandono', tarefa: tarefa, etapa: etapa, faixa_duracao: bucket(relogio() - t0) }); },
  };
}